import { ConditionProperties, RuleProperties } from 'json-rules-engine';
import {
  allFacts,
  lenderCampaignRules,
  lenderEndpointRules,
  ParamInfo,
  pricingRules,
  TypeInfo
} from './schema';

export const camelCaseToStartCase = (input: string): string => {
  // Removes any period and capitalizes the next character,
  //  e.g. "lead.leadID" becomes "leadLeadID"
  const replacePeriod = input.replace(/\.([a-z])/, (_, ch) => ch.toUpperCase());
  // Places a space between each lowercase and uppercase character,
  // e.g. "hasAcceptedOffer" becomes "has Accepted Offer"
  const startCase = replacePeriod.replace(/([a-z])([A-Z])/g, '$1 $2');
  // Capitalizes the first character of the string
  return startCase.charAt(0).toUpperCase() + startCase.slice(1);
};

const unaryOperators = ['isNone', 'isDefined', 'isNotNone', 'isNotDefined'];

const formatValue = (value: unknown, type: TypeInfo): string => {
  if (typeof value === 'object' && value != null && 'fact' in value) {
    return formatFact(
      (value as { fact: string }).fact,
      (value as { params?: Record<string, unknown> }).params
    );
  }
  if (value == null) {
    return `${value}`;
  }
  if (Array.isArray(value)) {
    return value.map((v) => formatValue(v, type.type === 'list' ? type.typeInfo : type)).join(', ');
  }
  switch (type.type) {
    case 'boolean':
      return !!value ? 'Yes' : 'No';
    case 'number':
      switch (type.unit) {
        case 'money':
          return Intl.NumberFormat('en-us', { style: 'currency', currency: 'USD' }).format(
            value as number
          );
        case 'timestamp':
          return new Date(value as number).toLocaleString('sv', { timeZone: 'America/New_York' });
        case 'percent':
          return `${(value as number) * 100}%`;
        case 'percentWhole':
          return `${value as number}%`;
      }
    case 'string':
      return `${value}${type.unit != null ? ` ${type.unit}` : ''}`;
    case 'object':
      return `{${Object.entries(type.properties)
        .map(
          ([key, typeInfo]) =>
            `${camelCaseToStartCase(key)}: ${formatValue(
              (value as Record<string, unknown>)[key],
              typeInfo
            )}`
        )
        .join(', ')}}`;
  }
  return `${value}`;
};

const formatFact = (fact: string, params?: Record<string, unknown>, path?: string): string => {
  const factInfo = allFacts.find(({ factId }) => factId === fact);
  return formatParameterizedType(`${fact}${path ? `.${path}` : ''}`, factInfo ?? {}, params);
};

const formatParameterizedType = (
  name: string,
  typeInfo: { params?: Record<string, ParamInfo> },
  params: Record<string, unknown> = {}
): string => {
  const paramKeys =
    typeInfo.params === undefined
      ? []
      : Object.entries(typeInfo.params!).filter(
          ([key, { optional }]) => !optional || params[key] !== undefined
        );
  return `${camelCaseToStartCase(name)}${
    paramKeys.length > 0
      ? ` with ${paramKeys
          .map(([key, type]) => `${camelCaseToStartCase(key)}: ${formatValue(params[key], type)}`)
          .join(', ')}`
      : ''
  }`;
};

export const formatConditionProps = ({
  fact,
  path,
  operator,
  value,
  params
}: ConditionProperties): string => {
  const factInfo = allFacts.find(({ factId }) => factId === fact) ?? { type: 'string' };
  return `${formatFact(fact, params ?? {}, path)} ${camelCaseToStartCase(operator)}${
    unaryOperators.includes(operator) ? '' : ` ${formatValue(value, factInfo)}`
  }`;
};

const allRuleTypes = [...lenderCampaignRules([]), ...pricingRules, ...lenderEndpointRules];
export const formatEventProps = ({ type, params }: RuleProperties['event']): string => {
  const typeInfo = allRuleTypes.find((info) => info.type === type) ?? {};
  return formatParameterizedType(type, typeInfo, params);
};
